import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useDevice } from '@/hooks';
import { authService } from '@/api/services';
import { AccountOperationType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { Modal } from '@/components/common/Modal/Modal';
import { PasskeyManager } from '@/components/features/auth/PasskeyManager';

// Stripe-hosted customer billing portal for device-service subscriptions. Same
// link the mobile app opens; billing is handled by Stripe, not in the portal.
const STRIPE_BILLING_PORTAL_URL = 'https://billing.stripe.com/p/login/bIY3g8ePg5HP96EbII';

type FeedbackType = { type: 'success' | 'error'; message: string } | null;

export function AccountSettingsPage() {
  const { username, accountUUID, logout } = useAuth();
  const { devices, selectedDeviceInfo } = useDevice();
  // Email is an account-level value (the same across the user's devices). Prefer
  // the loaded device info, but fall back to the account-devices list so it
  // still shows when no specific device is selected.
  const currentEmail =
    selectedDeviceInfo?.userInfo?.account?.emailStr ||
    devices?.[0]?.userInfo?.account?.emailStr ||
    null;
  const navigate = useNavigate();

  // ─── Billing ───
  // Device-service billing runs through Stripe's hosted customer portal. Open it
  // in a new tab (external, Stripe-hosted) — the same billing entry point the
  // mobile app links to.
  const handleManageBilling = () => {
    window.open(STRIPE_BILLING_PORTAL_URL, '_blank', 'noopener,noreferrer');
  };

  // ─── Change Password ───
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwFeedback, setPwFeedback] = useState<FeedbackType>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwFeedback(null);

    if (!currentPassword) {
      setPwFeedback({ type: 'error', message: 'Current password is required.' });
      return;
    }
    if (newPassword.length < 6) {
      setPwFeedback({ type: 'error', message: 'New password must be at least 6 characters.' });
      return;
    }
    if (newPassword.length > 29) {
      setPwFeedback({ type: 'error', message: 'New password must be 29 characters or less.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwFeedback({ type: 'error', message: 'New passwords do not match.' });
      return;
    }
    if (currentPassword === newPassword) {
      setPwFeedback({ type: 'error', message: 'New password must be different from current password.' });
      return;
    }

    setPwLoading(true);
    try {
      await authService.authenticatedAccountOperation({
        accountUUID: accountUUID ?? undefined,
        operationType: AccountOperationType.CHANGE_PASSWORD_AUTHENTICATED,
        userName: username ?? undefined,
        currPassword: currentPassword,
        newPassword: newPassword,
      });
      setPwFeedback({ type: 'success', message: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err instanceof Error ? err.message : '';
      const msg = detail.includes('PASSWORD_INVALID')
        ? 'Current password is incorrect.'
        : 'Failed to change password. Please try again.';
      setPwFeedback({ type: 'error', message: msg });
    } finally {
      setPwLoading(false);
    }
  };

  // ─── Change Email ───
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<FeedbackType>(null);

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailFeedback(null);

    if (!newEmail || !newEmail.includes('@')) {
      setEmailFeedback({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    if (newEmail.length > 50) {
      setEmailFeedback({ type: 'error', message: 'Email must be 50 characters or less.' });
      return;
    }

    setEmailLoading(true);
    try {
      await authService.authenticatedAccountOperation({
        accountUUID: accountUUID ?? undefined,
        operationType: AccountOperationType.CHANGE_EMAIL_AUTHENTICATED,
        userName: username ?? undefined,
        emailStr: newEmail,
      });
      setEmailFeedback({ type: 'success', message: 'Email address updated successfully.' });
      setNewEmail('');
    } catch {
      setEmailFeedback({ type: 'error', message: 'Failed to update email. Please try again.' });
    } finally {
      setEmailLoading(false);
    }
  };

  // ─── Delete Account ───
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState<FeedbackType>(null);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;

    setDeleteLoading(true);
    setDeleteFeedback(null);
    try {
      await authService.authenticatedAccountOperation({
        accountUUID: accountUUID ?? undefined,
        operationType: AccountOperationType.DELETE_ACCOUNT,
        userName: username ?? undefined,
      });
      setDeleteConfirmOpen(false);
      logout();
      navigate('/login', { replace: true });
    } catch {
      setDeleteFeedback({ type: 'error', message: 'Failed to delete account. Please try again.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Account Settings</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage your account preferences</p>
      </div>

      {/* Account Info */}
      <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-navy-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Account Information</h2>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500 dark:text-slate-400">Username</span>
            <span className="font-mono text-sm font-medium text-slate-800 dark:text-white">{username || '—'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500 dark:text-slate-400">Email Address</span>
            <span className="text-sm font-medium text-slate-800 dark:text-white">{currentEmail || '—'}</span>
          </div>
        </div>
      </div>

      {/* Billing */}
      <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-navy-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Billing</h2>
        </div>
        <div className="px-5 py-4">
          <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
            Manage your device service billing through our secure Stripe portal — update your
            payment method, view invoices, or change your subscription. Opens in a new tab.
          </p>
          <Button variant="secondary" onClick={handleManageBilling}>Manage Billing</Button>
        </div>
      </div>

      {/* Change Password */}
      <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-navy-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4 px-5 py-4">
          {pwFeedback && (
            <div className={`rounded-lg border p-3 text-sm ${
              pwFeedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
            }`}>
              {pwFeedback.message}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              autoComplete="current-password" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              autoComplete="new-password" />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">6–29 characters. No spaces, slashes, or quotes.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              autoComplete="new-password" />
          </div>
          <div className="pt-2">
            <Button type="submit" loading={pwLoading}>Change Password</Button>
          </div>
        </form>
      </div>

      {/* Change Email */}
      <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-navy-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Change Email Address</h2>
        </div>
        <form onSubmit={handleChangeEmail} className="space-y-4 px-5 py-4">
          {emailFeedback && (
            <div className={`rounded-lg border p-3 text-sm ${
              emailFeedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
            }`}>
              {emailFeedback.message}
            </div>
          )}
          {currentEmail && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Current email: <span className="font-medium text-slate-800 dark:text-white">{currentEmail}</span>
            </p>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">New Email Address</label>
            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="newemail@example.com"
              className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500" />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Max 50 characters. Used for alerts and account recovery.</p>
          </div>
          <div className="pt-2">
            <Button type="submit" loading={emailLoading}>Update Email</Button>
          </div>
        </form>
      </div>

      {/* Passkeys */}
      <PasskeyManager />

      {/* Delete Account */}
      <div className="rounded-xl border border-red-200 bg-white dark:border-red-800/30 dark:bg-navy-900">
        <div className="border-b border-red-200 px-5 py-4 dark:border-red-800/30">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">Danger Zone</h2>
        </div>
        <div className="px-5 py-4">
          <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
            Permanently delete your account and remove all device associations. This action cannot be undone.
          </p>
          {deleteFeedback && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {deleteFeedback.message}
            </div>
          )}
          <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>Delete Account</Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmOpen} onClose={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); }} title="Delete Account" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            This will permanently delete your account <span className="font-semibold text-slate-800 dark:text-white">{username}</span> and remove all device associations.
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Type <span className="font-mono font-bold text-red-600 dark:text-red-400">DELETE</span> to confirm:
          </p>
          <input type="text" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white" />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); }}>Cancel</Button>
            <Button variant="danger" loading={deleteLoading} disabled={deleteConfirmText !== 'DELETE'} onClick={handleDeleteAccount}>
              Delete My Account
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
